// Shared, single-source-of-truth Word-Home-ribbon formatting layer for every
// Rich Text Editor surface in the app (Workspace composer, Mail Merge main +
// modal editors, Sequences editor, and any future editor).
//
// WHY THIS FILE EXISTS: before this, four surfaces each registered their own,
// slightly different Quill font/size configuration (OutlookWorkspace.js had
// real named-font + point-size Attributors; MailMerge.js's main editor used a
// raw px-based whitelist; its "Email Messages" modal used a third, simpler
// config; SequenceBuilder.js used Quill's stock small/false/large/huge only).
// Quill's format registry is a single global singleton shared by the whole
// bundle (`Quill.register(..., true)` overrides app-wide, not per-file), so
// those four configs were silently fighting over the same 'font'/'size'
// format names. Centralizing registration here — called exactly once, from
// RichTextEditor.js — removes that footgun and gives every surface identical
// behavior.
//
// BACKWARD COMPATIBILITY: existing saved documents (Mail Merge templates,
// Sequence steps, Workspace drafts) already contain a mix of px-based,
// pt-based, and default-Quill-class-based size/font formatting in their
// stored HTML. None of the Attributors below use a `whitelist` restriction
// for size (an unrestricted Style Attributor accepts any value — see
// parchment's Attributor.canAdd: `if (this.whitelist == null) return true;`),
// so old content keeps rendering and remains editable exactly as before.
// FontClass keeps a whitelist (Quill's Class attributor requires one value
// per DOM class token), but it is a strict superset of every font slug any
// surface has ever used historically, so no existing content is orphaned.

let registered = false;

// ---- Fonts -----------------------------------------------------------
// "Theme Fonts" mirror Word's own default Office theme (heading font +
// body font, always pinned at the top of the picker). "Document Fonts"
// below covers the common fonts already in use across the app plus the
// rest of Word's default quick-pick list. `value` is used as the CSS class
// suffix (ql-font-<value>) so it must be a bare token — no spaces/symbols.
export const FONT_LIST = [
  { value: "calibrilight", label: "Calibri Light", css: "'Calibri Light',Calibri,sans-serif", theme: "heading" },
  { value: "calibri", label: "Calibri", css: "Calibri,sans-serif", theme: "body" },
  { value: "arial", label: "Arial", css: "Arial,Helvetica,sans-serif" },
  { value: "timesnewroman", label: "Times New Roman", css: "'Times New Roman',Times,serif" },
  { value: "georgia", label: "Georgia", css: "Georgia,serif" },
  { value: "verdana", label: "Verdana", css: "Verdana,Geneva,sans-serif" },
  { value: "tahoma", label: "Tahoma", css: "Tahoma,Geneva,sans-serif" },
  { value: "couriernew", label: "Courier New", css: "'Courier New',Courier,monospace" },
  { value: "trebuchetms", label: "Trebuchet MS", css: "'Trebuchet MS',sans-serif" },
  { value: "comicsansms", label: "Comic Sans MS", css: "'Comic Sans MS',cursive" },
  { value: "impact", label: "Impact", css: "Impact,Charcoal,sans-serif" },
  { value: "cambria", label: "Cambria", css: "Cambria,Georgia,serif" },
  { value: "garamond", label: "Garamond", css: "Garamond,Georgia,serif" },
  { value: "segoeui", label: "Segoe UI", css: "'Segoe UI',Tahoma,sans-serif" },
  { value: "helvetica", label: "Helvetica", css: "Helvetica,Arial,sans-serif" },
];
export const THEME_FONTS = FONT_LIST.filter(f => f.theme);
export const DOCUMENT_FONTS = FONT_LIST.filter(f => !f.theme);

// ---- Sizes -------------------------------------------------------------
// Word's exact Home-ribbon preset list. The ribbon combo box additionally
// allows typing any custom value (see WordRibbon.js's FontSizeBox) — the
// Attributor itself is unrestricted so a typed "13" round-trips fine.
export const SIZE_LIST = [
  "8","9","10","11","12","14","16","18","20","22","24","26","28","36","48","72",
].map(n => ({ value: n + "pt", label: n }));
export const DEFAULT_SIZE = "11pt";

// ---- Change Case modes (Word's "Aa" button) ----------------------------
export const CASE_MODES = [
  { id: "sentence", label: "Sentence case." },
  { id: "lower", label: "lowercase" },
  { id: "upper", label: "UPPERCASE" },
  { id: "capitalize", label: "Capitalize Each Word" },
  { id: "toggle", label: "tOGGLE cASE" },
];

// ---- Underline styles ----------------------------------------------------
export const UNDERLINE_STYLES = [
  { value: "single", label: "Single" },
  { value: "double", label: "Double" },
  { value: "thick", label: "Thick" },
  { value: "dotted", label: "Dotted" },
  { value: "dashed", label: "Dashed" },
  { value: "wavy", label: "Wavy" },
];

// ---- Text effects (curated subset of Word's larger gallery) -------------
export const TEXT_EFFECTS = [
  { value: "shadow", label: "Shadow" },
  { value: "outline", label: "Outline" },
  { value: "glow", label: "Glow" },
];

// ---- Bullet Library (Word's Home > Bullets gallery) ----------------------
// Real per-glyph bullet styles, not just the single built-in disc — matches
// Word's "Bullet Library" swatch grid (None / filled circle / hollow circle
// / filled square / diamond / arrow / check). Applied as a line-level class
// on top of the existing "list":"bullet" format (registerQuillFormats below
// registers the "bulletstyle" Attributor that puts these classes on); see
// WORD_RIBBON_CSS for the ::before content overrides that make each one
// actually render as that glyph.
export const BULLET_STYLES = [
  { id: "disc", label: "Filled Round", glyph: "●" },
  { id: "circle", label: "Hollow Round", glyph: "○" },
  { id: "square", label: "Filled Square", glyph: "■" },
  { id: "diamond", label: "Diamond", glyph: "◆" },
  { id: "arrow", label: "Arrow", glyph: "➤" },
  { id: "check", label: "Checkmark", glyph: "✓" },
];

// ---- Numbering Library (Word's Home > Numbering gallery) -----------------
// Same idea as bullets above, but for ordered lists — a handful of Word's
// standard numbering formats, applied as a line-level class (the "numstyle"
// Attributor below) that overrides the counter-style Quill's own
// quill.snow.css already uses for the top list level (see WORD_RIBBON_CSS).
export const NUMBER_FORMATS = [
  { id: "decimal", label: "1. 2. 3.", counterStyle: "decimal", suffix: ". " },
  { id: "decimalparen", label: "1) 2) 3)", counterStyle: "decimal", suffix: ") " },
  { id: "alpha", label: "a. b. c.", counterStyle: "lower-alpha", suffix: ". " },
  { id: "alphaupper", label: "A. B. C.", counterStyle: "upper-alpha", suffix: ". " },
  { id: "roman", label: "i. ii. iii.", counterStyle: "lower-roman", suffix: ". " },
];

// ---- Word-style color pickers (Theme Colors grid + Standard Colors) ------
// Real Word derives 5 tint/shade variants per theme color from the active
// Office theme via HSL math; this app has no theme engine, so these are a
// direct, hardcoded match to Office's own default theme palette (the exact
// values Word ships with out of the box) with tints/shades computed by a
// simple linear RGB mix toward white/black — visually the same "10 columns
// x 6 rows" grid Word's own Font Color / Highlight / Shading pickers show,
// just not driven by a swappable theme (nothing in this app has one).
const mix = (hex, toward, amt) => {
  const p = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
  const [r, g, b] = p(hex), [tr, tg, tb] = p(toward);
  const c = (a, ta) => Math.round(a + (ta - a) * amt).toString(16).padStart(2, "0");
  return "#" + c(r, tr) + c(g, tg) + c(b, tb);
};
const THEME_COLOR_BASE = [
  "#FFFFFF", "#000000", "#E7E6E6", "#44546A", "#4472C4", "#ED7D31", "#A5A5A5", "#FFC000", "#5B9BD5", "#70AD47",
];
// Column 0 = the base swatch; columns 1-5 = 5 tint/shade steps, lightest to
// darkest — white and black each need their own direction (there's nothing
// lighter than white or darker than black to mix toward).
export const THEME_COLOR_GRID = THEME_COLOR_BASE.map((hex) => {
  if (hex === "#FFFFFF") return [hex, "#F2F2F2", "#D9D9D9", "#BFBFBF", "#A6A6A6", "#808080"];
  if (hex === "#000000") return [hex, "#262626", "#404040", "#595959", "#7F7F7F", "#A6A6A6"];
  return [hex, mix(hex, "#FFFFFF", 0.6), mix(hex, "#FFFFFF", 0.4), mix(hex, "#FFFFFF", 0.2), mix(hex, "#000000", 0.25), mix(hex, "#000000", 0.5)];
});
export const STANDARD_COLORS = ["#C00000", "#FF0000", "#FFC000", "#FFFF00", "#92D050", "#00B050", "#00B0F0", "#0070C0", "#002060", "#7030A0"];

// ---- Quick Styles / Styles Gallery ---------------------------------------
// Each entry describes how to reset+apply a block-level "paragraph style" —
// mirrors Word's Quick Styles gallery on the Home ribbon.
export const STYLE_GALLERY = [
  { id: "normal", label: "Normal", format: { header: false, blockquote: false, pborder: false, pshading: false, lineheight: "" } },
  { id: "nospacing", label: "No Spacing", format: { header: false, blockquote: false, pborder: false, pshading: false, lineheight: "1", parabefore: false, paraafter: false } },
  { id: "heading1", label: "Heading 1", format: { header: 1, blockquote: false, pborder: false, pshading: false } },
  { id: "heading2", label: "Heading 2", format: { header: 2, blockquote: false, pborder: false, pshading: false } },
  { id: "title", label: "Title", format: { header: 1, blockquote: false, pborder: false, pshading: false, size: "28pt" } },
  { id: "subtitle", label: "Subtitle", format: { header: 2, blockquote: false, pborder: false, pshading: false, italic: true, size: "16pt" } },
  { id: "quote", label: "Quote", format: { header: false, blockquote: true, pborder: false, pshading: false } },
  { id: "intensequote", label: "Intense Quote", format: { header: false, blockquote: true, pborder: "2px solid currentColor", pshading: "rgba(0,120,212,.06)" } },
];

// CSS injected once (see RichTextEditor.js). Covers: theme-font labels in
// our own combobox (no Quill-picker CSS hacks needed since WordRibbon is a
// fully custom UI, not Quill's built-in toolbar module), multilevel bullet
// cycling (Quill's own quill.snow.css already cycles ordered-list numbering
// decimal -> lower-alpha -> lower-roman per indent depth — Word's own
// default behavior — so only bullet glyphs needed a matching override),
// underline-style variants, text effects, and the Show/Hide formatting-marks
// pilcrow.
export const WORD_RIBBON_CSS = `
${FONT_LIST.map(f => `.ql-font-${f.value}{font-family:${f.css};}`).join("")}
.ql-editor ul>li.ql-indent-1::before{content:'\\25CB';}
.ql-editor ul>li.ql-indent-2::before{content:'\\25AA';}
.ql-editor ul>li.ql-indent-4::before{content:'\\25CB';}
.ql-editor ul>li.ql-indent-5::before{content:'\\25AA';}
.ql-editor ul>li.ql-indent-7::before{content:'\\25CB';}
.ql-editor ul>li.ql-indent-8::before{content:'\\25AA';}
.ql-editor ul>li.ql-bullet-disc::before{content:'\\2022';}
.ql-editor ul>li.ql-bullet-circle::before{content:'\\25CB';}
.ql-editor ul>li.ql-bullet-square::before{content:'\\25AA';}
.ql-editor ul>li.ql-bullet-diamond::before{content:'\\25C6';}
.ql-editor ul>li.ql-bullet-arrow::before{content:'\\27A4';}
.ql-editor ul>li.ql-bullet-check::before{content:'\\2713';}
.ql-editor ol li.ql-num-decimal:before{content:counter(list-0,decimal) '. ';}
.ql-editor ol li.ql-num-decimalparen:before{content:counter(list-0,decimal) ') ';}
.ql-editor ol li.ql-num-alpha:before{content:counter(list-0,lower-alpha) '. ';}
.ql-editor ol li.ql-num-alphaupper:before{content:counter(list-0,upper-alpha) '. ';}
.ql-editor ol li.ql-num-roman:before{content:counter(list-0,lower-roman) '. ';}
.ql-u-double{text-decoration-line:underline;text-decoration-style:double;}
.ql-u-thick{text-decoration-line:underline;text-decoration-thickness:3px;}
.ql-u-dotted{text-decoration-line:underline;text-decoration-style:dotted;}
.ql-u-dashed{text-decoration-line:underline;text-decoration-style:dashed;}
.ql-u-wavy{text-decoration-line:underline;text-decoration-style:wavy;}
.ql-texteffect-shadow{text-shadow:2px 2px 3px rgba(0,0,0,.45);}
.ql-texteffect-outline{-webkit-text-stroke:1px currentColor;color:transparent;}
.ql-texteffect-glow{text-shadow:0 0 6px currentColor,0 0 14px currentColor;}
.sdr-show-marks .ql-editor p::after,.sdr-show-marks .ql-editor li::after{content:'\\00B6';color:#8890B0;margin-left:3px;font-weight:400;}
.ql-editor hr.ql-hr-divider{border:none;border-top:1px solid #d1cec9;margin:12px 0;display:block;width:100%;height:0;}
`;

// Registers every custom Attributor this file needs with Quill's global
// registry. Idempotent and safe to call from every surface that imports
// RichTextEditor.js — only the first call does anything.
export function registerQuillFormats(ReactQuillOrQuill) {
  if (registered) return;
  try {
    const Quill = ReactQuillOrQuill?.Quill || ReactQuillOrQuill;
    if (!Quill) return;
    const Parchment = Quill.import("parchment");

    const FontClass = new Parchment.Attributor.Class("font", "ql-font", {
      scope: Parchment.Scope.INLINE,
      whitelist: FONT_LIST.map(f => f.value),
    });
    Quill.register(FontClass, true);

    // No whitelist -> accepts any value (px, pt, or unitless), preserving
    // every pre-existing size format already saved across the app.
    const SizeStyle = new Parchment.Attributor.Style("size", "font-size", {
      scope: Parchment.Scope.INLINE,
    });
    Quill.register(SizeStyle, true);

    const LineHeightStyle = new Parchment.Attributor.Style("lineheight", "line-height", {
      scope: Parchment.Scope.BLOCK,
    });
    Quill.register(LineHeightStyle, true);

    const ParaShadingStyle = new Parchment.Attributor.Style("pshading", "background-color", {
      scope: Parchment.Scope.BLOCK,
    });
    Quill.register(ParaShadingStyle, true);

    const ParaBorderStyle = new Parchment.Attributor.Style("pborder", "border", {
      scope: Parchment.Scope.BLOCK,
    });
    Quill.register(ParaBorderStyle, true);

    // Word's Paragraph-dialog "Spacing Before/After" — independent of line
    // spacing (lineheight, above). No whitelist, same unrestricted pattern
    // as the other Style Attributors here, so any pre-existing value keeps
    // round-tripping. Both are Style (inline) Attributors, so — same as
    // size/lineheight/pshading/pborder — they're already email-safe with no
    // extra work needed from the emailHtml.js send-time transform.
    const ParaBeforeStyle = new Parchment.Attributor.Style("parabefore", "margin-top", {
      scope: Parchment.Scope.BLOCK,
    });
    Quill.register(ParaBeforeStyle, true);

    const ParaAfterStyle = new Parchment.Attributor.Style("paraafter", "margin-bottom", {
      scope: Parchment.Scope.BLOCK,
    });
    Quill.register(ParaAfterStyle, true);

    const UnderlineStyleClass = new Parchment.Attributor.Class("underlinestyle", "ql-u", {
      scope: Parchment.Scope.INLINE,
      whitelist: UNDERLINE_STYLES.map(s => s.value).filter(v => v !== "single"),
    });
    Quill.register(UnderlineStyleClass, true);

    // Bullet Library / Numbering Library (Word's Home-ribbon gallery
    // dropdowns) — a line-level class layered on top of the existing
    // "list":"bullet"/"ordered" format; see WORD_RIBBON_CSS above for the
    // ::before content overrides these classes actually drive. BLOCK scope
    // matches every other line-level Attributor in this file (pborder,
    // pshading, lineheight); unrestricted-vs-whitelist doesn't matter here
    // since both are Class Attributors, which always require a whitelist.
    const BulletStyleClass = new Parchment.Attributor.Class("bulletstyle", "ql-bullet", {
      scope: Parchment.Scope.BLOCK,
      whitelist: BULLET_STYLES.map(b => b.id),
    });
    Quill.register(BulletStyleClass, true);

    const NumStyleClass = new Parchment.Attributor.Class("numstyle", "ql-num", {
      scope: Parchment.Scope.BLOCK,
      whitelist: NUMBER_FORMATS.map(n => n.id),
    });
    Quill.register(NumStyleClass, true);

    const TextEffectClass = new Parchment.Attributor.Class("texteffect", "ql-texteffect", {
      scope: Parchment.Scope.INLINE,
      whitelist: TEXT_EFFECTS.map(e => e.value),
    });
    Quill.register(TextEffectClass, true);

    // Word/Outlook's "Insert Horizontal Line" — a thin <hr> block embed,
    // available app-wide via WordRibbon.js's Paragraph group button and via
    // typing "---" then Enter (see RichTextEditor.js's auto-divider
    // handler), matching Word's own AutoFormat-As-You-Type behavior. Kept as
    // a single shared registration (rather than one per surface) for the
    // same reason every other format here is centralized — Mail Merge used
    // to register its own local copy of this exact blot; that local
    // registration has been removed in favor of this one so "divider"
    // isn't fought over by two different registrations of the same name.
    const BlockEmbed = Quill.import("blots/block/embed");
    class DividerBlot extends BlockEmbed {}
    DividerBlot.blotName = "divider";
    DividerBlot.tagName = "hr";
    DividerBlot.className = "ql-hr-divider";
    Quill.register(DividerBlot, true);

    registered = true;
  } catch (e) {
    // Never let a formatting-registry issue break the editor itself —
    // surfaces still work with Quill's stock formats if this fails.
    console.error("registerQuillFormats failed:", e);
  }
}

// ---- Action helpers (not persistent formats — one-shot text operations) --

// Inserts a horizontal-line divider (the "divider" blot registered above) at
// `index` (defaults to the current selection, or end-of-document if there is
// none) and leaves the cursor on a fresh empty line right after it — mirrors
// Word/Outlook's own Insert > Horizontal Line behavior. Used both by
// WordRibbon.js's ribbon button (manual insert) and by RichTextEditor.js's
// "---" + Enter auto-divider handler (typed shorthand), so both paths always
// produce an identical result.
//
// A block embed (which "divider" is) must sit on a line of its own — every
// block in Quill's Delta model is terminated by a "\n", and manually
// splicing insertEmbed/insertText calls at a raw index (the first version of
// this helper) produced stray blank paragraphs whenever the cursor wasn't
// already sitting at a clean line boundary (e.g. right after text you just
// typed, the common case for the ribbon button) — Quill's own internal
// block-boundary fixup and this helper's manual one ended up compensating
// for each other. Routing through `clipboard.dangerouslyPasteHTML` instead
// sidesteps all of that: it's the exact same HTML-to-Delta conversion path
// every real paste in this app already goes through (see pasteClean.js's
// own fallback use of the same call, and OutlookWorkspace.js's proven
// "---"+Enter divider shorthand, which has used this exact approach for its
// own standalone editor all along), so Quill's own well-tested parser
// figures out correct block boundaries regardless of where the cursor is.
export function insertHorizontalLine(quill, index) {
  if (!quill) return;
  const sel = quill.getSelection(true);
  const idx = typeof index === "number" ? index : (sel ? sel.index : quill.getLength());
  quill.clipboard.dangerouslyPasteHTML(idx, '<hr class="ql-hr-divider"><p><br></p>', "user");
  quill.setSelection(idx + 2, 0, "user");
}

export function changeCase(quill, mode) {
  if (!quill) return;
  const range = quill.getSelection(true);
  if (!range || !range.length) return;
  const text = quill.getText(range.index, range.length);
  let out = text;
  if (mode === "lower") out = text.toLowerCase();
  else if (mode === "upper") out = text.toUpperCase();
  else if (mode === "capitalize") out = text.replace(/\w\S*/g, w => w[0].toUpperCase() + w.slice(1).toLowerCase());
  else if (mode === "sentence") {
    out = text.toLowerCase().replace(/(^\s*\w|[.!?]\s+\w)/g, c => c.toUpperCase());
  } else if (mode === "toggle") {
    out = text.split("").map(c => (c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase())).join("");
  }
  if (out === text) return;
  const fmt = quill.getFormat(range.index, 1);
  quill.deleteText(range.index, range.length, "user");
  quill.insertText(range.index, out, fmt, "user");
  quill.setSelection(range.index, out.length, "silent");
}

// Sorts the lines (paragraphs / list items) touched by the current
// selection alphabetically by their plain-text content, preserving each
// line's own inline formatting (bold runs, colors, etc. move with their
// line). No selection -> sorts the whole document's top-level lines.
export function sortSelectionLines(quill) {
  if (!quill) return;
  const Delta = quill.constructor.import ? quill.constructor.import("delta") : null;
  const range = quill.getSelection(true) || { index: 0, length: quill.getLength() };
  const lines = quill.getLines(range.index, Math.max(range.length, 1));
  if (!lines || lines.length < 2) return;
  const first = lines[0], last = lines[lines.length - 1];
  const start = quill.getIndex(first);
  const end = quill.getIndex(last) + last.length();
  const total = end - start;
  const entries = lines.map(line => {
    const idx = quill.getIndex(line);
    const len = line.length();
    return { text: quill.getText(idx, len).trim(), ops: quill.getContents(idx, len).ops };
  });
  entries.sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: "base", numeric: true }));
  if (Delta) {
    let combined = new Delta();
    entries.forEach(e => { combined = combined.concat(new Delta(e.ops)); });
    quill.updateContents(new Delta().retain(start).delete(total).concat(combined), "user");
  } else {
    // Fallback if the Delta class isn't importable in this Quill build.
    let offset = start;
    entries.forEach(e => {
      const plain = e.ops.map(o => (typeof o.insert === "string" ? o.insert : "")).join("");
      quill.deleteText(offset, plain.length, "user");
      quill.insertText(offset, plain, "user");
      offset += plain.length;
    });
  }
}

export function applyStyleGallery(quill, styleId) {
  if (!quill) return;
  const style = STYLE_GALLERY.find(s => s.id === styleId);
  if (!style) return;
  const range = quill.getSelection(true);
  if (!range) return;
  Object.entries(style.format).forEach(([key, val]) => {
    if (["header", "blockquote", "pborder", "pshading", "lineheight", "parabefore", "paraafter"].includes(key)) {
      quill.formatLine(range.index, Math.max(range.length, 1), key, val, "user");
    } else {
      quill.formatText(range.index, Math.max(range.length, 1), key, val, "user");
    }
  });
}
